import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return {
        headers: {
            Authorization: `Bearer ${token}`
        }
    };
};

// Get all classes for the current center
export const getClasses = async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.tutor_id) queryParams.append('tutor_id', params.tutor_id);

    const url = queryParams.toString()
        ? `${API_URL}/classes?${queryParams.toString()}`
        : `${API_URL}/classes`;

    const response = await axios.get(url, getAuthHeader());
    return response.data;
};

// Get a single class by ID
export const getClass = async (classId) => {
    const response = await axios.get(`${API_URL}/classes/${classId}`, getAuthHeader());
    return response.data;
};

// Get all events for a specific class
export const getClassEvents = async (classId) => {
    const response = await axios.get(`${API_URL}/classes/${classId}/events`, getAuthHeader());
    return response.data;
};

// Get enrollments for a specific class
// Note: Backend doesn't have a direct GET endpoint for class enrollments.
// Since class enrollments apply to ALL future events, we fetch the next
// upcoming event's enrollments to represent the class enrollment list.
export const getClassEnrollments = async (classId) => {
    try {
        // Get all events for this class
        const eventsResponse = await axios.get(`${API_URL}/classes/${classId}/events`, getAuthHeader());
        const events = eventsResponse.data;

        if (events.length === 0) {
            return [];
        }

        // Find the next upcoming event (or most recent if all are past)
        const now = new Date();
        const futureEvents = events
            .filter(event => new Date(event.start_datetime) >= now)
            .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));

        // If no future events, get the most recent past event
        const targetEvent = futureEvents.length > 0
            ? futureEvents[0]
            : events.sort((a, b) => new Date(b.start_datetime) - new Date(a.start_datetime))[0];

        // Fetch enrollments for this single event
        const enrollmentsResponse = await axios.get(
            `${API_URL}/events/${targetEvent.id}/enrollments`,
            getAuthHeader()
        );

        return enrollmentsResponse.data;
    } catch (error) {
        throw error;
    }
};

// Record student attendance
export const recordAttendance = async (classId, attendanceData) => {
    const response = await axios.post(
        `${API_URL}/classes/${classId}/attendance`,
        attendanceData, getAuthHeader());
    return response.data;
};

// Get attendance records for a class
export const getClassAttendance = async (classId) => {
    const response = await axios.get(`${API_URL}/classes/${classId}/attendance`,getAuthHeader());
    return response.data;
};

// Create a new class
// classData should include: course_id, tutor_user_id, name, schedule_details,
// start_date, end_date, start_time, end_time, day_of_the_week, status
export const createClass = async (classData) => {
    const response = await axios.post(
        `${API_URL}/classes`,
        classData, getAuthHeader());
    return response.data;
};

// Update a class
export const updateClass = async (classId, classData) => {
    const response = await axios.put(
        `${API_URL}/classes/${classId}`,
        classData,
        getAuthHeader()
    );
    return response.data;
};

// Delete a class
export const deleteClass = async (classId) => {
    const response = await axios.delete(
        `${API_URL}/classes/${classId}`,
        getAuthHeader()
    );
    return response.data;
};

// Enroll a student in a class
export const enrollStudent = async (classId, studentUserId) => {
    const response = await axios.post(
        `${API_URL}/classes/${classId}/enrollments`,
        { student_user_id: studentUserId },
        getAuthHeader()
    );
    return response.data;
};

// Remove a student enrollment
export const unenrollStudent = async (classId, enrollmentId) => {
    const response = await axios.delete(
        `${API_URL}/classes/${classId}/enrollments/${enrollmentId}`,
        getAuthHeader()
    );
    return response.data;
};

// Delete enrollment by enrollment ID (simpler endpoint)
export const deleteEnrollment = async (enrollmentId) => {
    const response = await axios.delete(
        `${API_URL}/enrollments/${enrollmentId}`,
        getAuthHeader()
    );
    return response.data;
};

export const deleteStudentFromAllFutureClasses = async (classId, studentId) => {
    const response = await axios.delete(
        `${API_URL}/classes/${classId}/enrollments/${studentId}/future`,
        getAuthHeader()
    );
    return response.data;
};
